import React, { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import "./Topbar.css";

import { clearAuth } from "../../store/authSlice";
import { persistor } from "../../store/store";
import type { RootState } from "../../store/store";
import {
  canWriteFinanceiroFromToken,
  getRevokedModulesFromToken,
  getRolesFromToken,
  isAdminFromToken,
  isRgbUserFromToken,
} from "../../utils/authToken";
import { LOGINPAGE } from "../../views/auth/routes";
import { useStaffProfile } from "../../hooks/useStaffProfile";

import EcoLogoWhite from "../../img/eco/logo-white.png";
import EcoIcon, { EcoIconName } from "../eco-icon/EcoIcon";
import NotificationsBell from "./NotificationsBell";
import { OPERACOES_PAGE } from "../../views/operacoes/routes";
import { PROCESSOS_ZE_PAGE } from "../../views/processos-ze/routes";
import { ESTOQUE_PAGE } from "../../views/estoque/routes";
import { DP_PAGE } from "../../views/dp/routes";
import { FINANCEIRO_PAGE } from "../../views/financeiro/routes";
import { ADMIN_SISTEMA_PAGE } from "../../views/admin-sistema/routes";
import { CAMPANHAS_PAGE } from "../../views/campanhas/routes";
import { SubmenuGroup, SubmenuLink, TOPBAR_SUBMENUS, supervisorEstoqueSubmenu } from "./topbarMenus";
import { ESTOQUE_BALANCO_LOJAS_PAGE } from "../../views/estoque/routes";
import { RGB_HOME_PAGE } from "../../views/rgb/routes";
import { moduleForPath, type AccessModule } from "../../utils/accessModules";

type NavKey = "home" | "operacoes" | "processos" | "dp" | "financeiro" | "estoque" | "campanhas" | "administracao";

type NavItem = {
  key: NavKey;
  label: string;
  /** Rótulo curto do celular, onde os módulos viram pílulas roláveis. */
  shortLabel?: string;
  /** Rótulo do desktop compacto (`ecoTopbar--compact`), quando a barra não cabe inteira. */
  compactLabel?: string;
  icon: EcoIconName;
  to: string;
  /** Papéis que podem abrir o item. Ausente = liberado para qualquer papel. */
  allowedRoles?: string[];
  /**
   * Quando o papel não está em `allowedRoles`: por padrão o item aparece
   * desabilitado (comportamento histórico de "Operações" para o supervisor);
   * com esta flag, some do menu (DP e Administração).
   */
  hideWhenDenied?: boolean;
  /** Módulo do item: some quando retirado do administrador restrito (ADR 0027). */
  module?: AccessModule;
};

const ITEMS: NavItem[] = [
  { key: "home", label: "Início", icon: "home", to: "/" },
  { key: "operacoes", label: "Operações", icon: "gear", to: OPERACOES_PAGE(), allowedRoles: ["ADMIN"], module: "OPERACOES" },
  { key: "processos", label: "Processos Zé", icon: "file", to: PROCESSOS_ZE_PAGE(), module: "PROCESSOS" },
  {
    key: "dp",
    label: "Departamento Pessoal",
    shortLabel: "DP",
    compactLabel: "Dep. Pessoal",
    icon: "users",
    to: DP_PAGE(),
    allowedRoles: ["ADMIN", "DP"],
    hideWhenDenied: true,
    module: "DP",
  },
  {
    key: "financeiro",
    label: "Financeiro",
    icon: "dollar",
    to: FINANCEIRO_PAGE(),
    allowedRoles: ["ADMIN", "FINANCEIRO", "FINANCEIRO_LEITURA"],
    hideWhenDenied: true,
    module: "FINANCEIRO",
  },
  { key: "estoque", label: "Estoque", icon: "box", to: ESTOQUE_PAGE(), module: "ESTOQUE" },
  {
    key: "campanhas",
    label: "Campanhas",
    icon: "megaphone",
    to: CAMPANHAS_PAGE(),
    allowedRoles: ["ADMIN", "GESTOR_CAMPANHAS"],
    hideWhenDenied: true,
    module: "CAMPANHAS",
  },
  {
    key: "administracao",
    label: "Administração",
    icon: "shield",
    to: ADMIN_SISTEMA_PAGE(),
    allowedRoles: ["ADMIN"],
    hideWhenDenied: true,
    module: "ADMINISTRACAO",
  },
];

/**
 * Telas operacionais com rota fora de `/operacoes` (herança do app antigo):
 * marcam "Operações" como ativo, como a Sidebar fazia via `activeKey`.
 */
const OPERACOES_LEGACY_PREFIXES = [
  "/agenda",
  "/freelancer-control",
  "/slots-manager",
  "/update-presence-and-values",
  "/evalaution-forms-admin-page",
  "/freelancer-ratings",
];

/** Telas do supervisor (rotas fora de `/home`): marcam "Início". */
const SUPERVISOR_PREFIXES = ["/supervisor", "/update-presence"];

const activeKeyFor = (pathname: string): NavKey | undefined => {
  if (pathname === "/" || pathname.startsWith("/home")) return "home";
  if (SUPERVISOR_PREFIXES.some((p) => pathname.startsWith(p))) return "home";
  if (pathname.startsWith("/operacoes")) return "operacoes";
  if (OPERACOES_LEGACY_PREFIXES.some((p) => pathname.startsWith(p))) return "operacoes";
  if (pathname.startsWith("/processos")) return "processos";
  if (pathname.startsWith("/dp")) return "dp";
  if (pathname.startsWith("/financeiro")) return "financeiro";
  if (pathname.startsWith("/estoque")) return "estoque";
  if (pathname.startsWith("/campanhas")) return "campanhas";
  if (pathname.startsWith("/administracao")) return "administracao";
  return undefined;
};

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrador",
  ADMIN_RESTRITO: "Administrador restrito",
  SUPERVISOR: "Supervisor",
  SUPERVISOR_DE_CAMPO: "Supervisor de campo",
  DP: "Departamento Pessoal",
  FINANCEIRO: "Financeiro",
  FINANCEIRO_LEITURA: "Financeiro (leitura)",
  GESTOR_CAMPANHAS: "Gestor de campanhas",
};

/**
 * Barra de navegação do staff, no topo de toda tela do admin (substituiu a
 * Sidebar). Montada uma vez pelo `StaffLayout`; as telas não a importam.
 *
 * Publica a própria altura em `--eco-topbar-h` (no `<html>`), para os elementos
 * sticky das telas pararem logo abaixo dela e os roots de `min-height: 100vh`
 * descontarem a barra — ela quebra em duas linhas em telas estreitas.
 */
export default function Topbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const headerRef = useRef<HTMLElement>(null);

  // Nome via `/auth/me`; até responder, a parte local do e-mail.
  const user = useStaffProfile();
  const token = useSelector((s: RootState) => s.auth.accessToken);
  const isAdmin = isAdminFromToken(token);
  const isRgbUser = isRgbUserFromToken(token);
  const canWriteFin = canWriteFinanceiroFromToken(token);
  const userType = user.userType;
  // Papéis efetivos (principal + adicionais, ADR 0024): é o que libera os itens.
  const roles = useMemo(() => getRolesFromToken(token), [token]);
  const revokedModules = useMemo(() => getRevokedModulesFromToken(token), [token]);
  const activeKey = activeKeyFor(pathname);

  /**
   * A barra fica numa linha só (decisão do usuário, 05/10/2026: nada de barra
   * "quebrada"). Medido, e não por breakpoint, porque a largura depende de
   * quantos módulos o papel vê. Em ordem, a primeira que couber:
   * 1. normal;
   * 2. `ecoTopbar--compact`: sem as setas dos submenus (o submenu continua
   *    abrindo), espaçamento menor e rótulo curto ("Dep. Pessoal");
   * 3. `ecoTopbar--stacked`: módulos numa segunda linha — último recurso, só em
   *    tela estreita onde nem o compacto cabe (no ADMIN, abaixo de ~1350px).
   * O celular (≤720px) tem layout próprio e ignora as duas classes.
   */
  const fitLayout = useCallback(() => {
    const el = headerRef.current;
    const inner = el?.querySelector<HTMLElement>(".ecoTopbar__inner");
    const logo = el?.querySelector<HTMLElement>(".ecoTopbar__logo");
    const nav = el?.querySelector<HTMLElement>(".ecoTopbar__nav");
    const account = el?.querySelector<HTMLElement>(".ecoTopbar__account");
    if (!el || !inner || !logo || !nav || !account) return;

    // Os itens têm `flex: 0 0 auto` e `nowrap`: a largura deles não depende de a
    // barra estar em uma ou duas linhas, só das classes de tamanho.
    const measure = () => {
      const innerStyle = getComputedStyle(inner);
      const available =
        inner.clientWidth - parseFloat(innerStyle.paddingLeft) - parseFloat(innerStyle.paddingRight);
      const innerGap = parseFloat(innerStyle.columnGap) || 0;
      const navGap = parseFloat(getComputedStyle(nav).columnGap) || 0;
      const items = Array.from(nav.children) as HTMLElement[];
      const navWidth =
        items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) +
        navGap * Math.max(0, items.length - 1);
      return {
        oneLine: logo.offsetWidth + navWidth + account.offsetWidth + innerGap * 2 <= available,
        navRowFits: navWidth <= available,
      };
    };

    el.classList.remove("ecoTopbar--compact", "ecoTopbar--stacked");
    const normal = measure();
    if (normal.oneLine) return;
    el.classList.add("ecoTopbar--compact");
    if (measure().oneLine) return;
    // Duas linhas: com rótulos inteiros se a linha dos módulos comportar; senão,
    // compacta também, para os módulos não quebrarem dentro da segunda linha.
    el.classList.add("ecoTopbar--stacked");
    if (normal.navRowFits) el.classList.remove("ecoTopbar--compact");
  }, []);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      fitLayout();
      root.style.setProperty("--eco-topbar-h", `${el.offsetHeight}px`);
    };
    update();
    // No quadro seguinte: trocar a classe muda a altura da própria barra, e
    // fazer isso dentro do callback dispara "ResizeObserver loop completed".
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      root.style.removeProperty("--eco-topbar-h");
    };
  }, [fitLayout]);

  // Itens mudam com o papel e com a tela ativa (o ativo fica em negrito, mais largo).
  useLayoutEffect(() => {
    fitLayout();
  });

  const canAccess = useCallback(
    (item: NavItem) => !item.allowedRoles || item.allowedRoles.some((role) => roles.includes(role)),
    [roles],
  );

  // Só supervisor (sem papel adicional): os hubs são de ADMIN, então a barra
  // mostra Início e o Estoque que ele abre, e nada desabilitado.
  const isSupervisorOnly =
    roles.length > 0 && roles.every((role) => role === "SUPERVISOR" || role === "SUPERVISOR_DE_CAMPO");
  const isFieldSupervisor = roles.includes("SUPERVISOR_DE_CAMPO");
  // Só gestor de campanhas: a barra mostra só o módulo dele.
  const isCampanhasOnly = roles.length > 0 && roles.every((role) => role === "GESTOR_CAMPANHAS");

  // Módulo retirado some de vez, como o que o papel não alcança.
  const visibleItems = useMemo(() => {
    if (isSupervisorOnly) {
      return ITEMS.filter((item) => item.key === "home" || item.key === "estoque").map((item) =>
        item.key === "estoque"
          ? { ...item, to: isFieldSupervisor ? ESTOQUE_BALANCO_LOJAS_PAGE() : RGB_HOME_PAGE() }
          : item,
      );
    }
    if (isCampanhasOnly) return ITEMS.filter((item) => item.key === "campanhas");
    return ITEMS.filter(
      (item) =>
        !(item.module && revokedModules.includes(item.module)) && (canAccess(item) || !item.hideWhenDenied),
    );
  }, [canAccess, revokedModules, isSupervisorOnly, isFieldSupervisor, isCampanhasOnly]);

  /** Submenu do item com as regras de acesso do hub aplicadas; grupo vazio some. */
  const submenuFor = useCallback(
    (key: NavKey): SubmenuGroup[] => {
      if (isSupervisorOnly) return key === "estoque" ? supervisorEstoqueSubmenu(isFieldSupervisor) : [];
      const allowed = (link: SubmenuLink) =>
        (!link.requiresAdmin || isAdmin) &&
        (!link.requiresRgb || isRgbUser) &&
        (!link.requiresFinWrite || canWriteFin) &&
        // Atalho para outro módulo (ex.: "Colaboradores CLT" em Administração).
        !revokedModules.includes(moduleForPath(link.to.split("?")[0]) ?? "");
      return (TOPBAR_SUBMENUS[key] ?? [])
        .map((group) => ({ ...group, links: group.links.filter(allowed) }))
        .filter((group) => group.links.length > 0);
    },
    [isAdmin, isRgbUser, canWriteFin, revokedModules, isSupervisorOnly, isFieldSupervisor],
  );

  const handleNavigate = (event: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    // Ctrl/Cmd/Shift/botão do meio: deixa o navegador abrir em nova aba.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    // Tira o foco do link: o submenu também abre por :focus-within e ficaria
    // aberto na tela nova depois que o mouse saísse.
    event.currentTarget.blur();
    navigate(to);
  };

  const isCurrent = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  const handleLogout = async () => {
    dispatch(clearAuth());
    await persistor.purge();
    toast.success("Você saiu.");
    navigate(LOGINPAGE(), { replace: true });
  };

  const labelOf = (role: string) => ROLE_LABELS[role] ?? role;
  // Rótulo do papel principal; os adicionais aparecem como "+N" e por extenso no title.
  // O ADMIN implícito do administrador restrito não é adicional.
  const extraRoles = roles.filter((role) => role !== userType && !(userType === "ADMIN_RESTRITO" && role === "ADMIN"));
  const roleLabel = labelOf(userType);
  const roleTitle = [userType, ...extraRoles].map(labelOf).join(" + ");

  return (
    <header ref={headerRef} className="ecoTopbar">
      <div className="ecoTopbar__inner">
        <img className="ecoTopbar__logo" src={EcoLogoWhite} alt="Eco System Serviços" />

        <nav className="ecoTopbar__nav" aria-label="Módulos">
          {visibleItems.map((item) => {
            const isActive = item.key === activeKey;

            if (!canAccess(item)) {
              return (
                <button
                  key={item.key}
                  type="button"
                  className="ecoTopbar__item"
                  disabled
                  title="Sem permissão"
                >
                  <EcoIcon name={item.icon} size={20} />
                  <span className="ecoTopbar__label">{item.label}</span>
                  <span className="ecoTopbar__compactLabel">{item.compactLabel ?? item.label}</span>
                  <span className="ecoTopbar__shortLabel">{item.shortLabel ?? item.label}</span>
                </button>
              );
            }

            const submenu = submenuFor(item.key);
            const link = (
              <a
                href={item.to}
                className={`ecoTopbar__item ${isActive ? "is-active" : ""}`}
                aria-current={isActive ? "page" : undefined}
                onClick={(event) => handleNavigate(event, item.to)}
              >
                <EcoIcon name={item.icon} size={20} />
                <span className="ecoTopbar__label">{item.label}</span>
                <span className="ecoTopbar__compactLabel">{item.compactLabel ?? item.label}</span>
                <span className="ecoTopbar__shortLabel">{item.shortLabel ?? item.label}</span>
                {submenu.length > 0 ? (
                  <EcoIcon name="chevronRight" size={14} className="ecoTopbar__caret" />
                ) : null}
              </a>
            );

            if (submenu.length === 0) return <React.Fragment key={item.key}>{link}</React.Fragment>;

            return (
              <div key={item.key} className="ecoTopbar__menu">
                {link}
                <div className="ecoTopbar__dropdown">
                  <div
                    className={`ecoTopbar__panel ${submenu.length > 1 ? "ecoTopbar__panel--cols" : ""}`}
                    aria-label={`Atalhos de ${item.label}`}
                    role="group"
                  >
                    {submenu.map((group) => (
                      <div key={group.title} className="ecoTopbar__group">
                        <span className="ecoTopbar__groupTitle">{group.title}</span>
                        {group.links.map((sub) => (
                          <a
                            key={sub.to + sub.label}
                            href={sub.to}
                            className={`ecoTopbar__subLink ${isCurrent(sub.to) ? "is-current" : ""}`}
                            aria-current={isCurrent(sub.to) ? "page" : undefined}
                            onClick={(event) => handleNavigate(event, sub.to)}
                          >
                            {sub.label}
                          </a>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="ecoTopbar__account">
          <NotificationsBell />

          {user.display ? (
            <div className="ecoTopbar__user" title={`${user.display} · ${roleTitle}`}>
              <span className="ecoTopbar__avatar" aria-hidden="true">
                {user.initials}
              </span>
              <span className="ecoTopbar__userText">
                <span className="ecoTopbar__userName">{user.display}</span>
                <span className="ecoTopbar__userRole">
                  {roleLabel}
                  {extraRoles.length ? ` +${extraRoles.length}` : ""}
                </span>
              </span>
            </div>
          ) : null}

          <button type="button" className="ecoTopbar__logout" onClick={handleLogout} aria-label="Sair" title="Sair">
            <EcoIcon name="logout" size={20} />
          </button>
        </div>
      </div>
    </header>
  );
}
